import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../api.js'
import { useSitzung } from '../sitzung.jsx'
import Kopfzeile from '../Kopfzeile.jsx'
import Dialog from '../Dialog.jsx'
import Icon from '../Icon.jsx'
import Unterschrift from '../Unterschrift.jsx'
import Uhrzeitwahl from '../Uhrzeitwahl.jsx'
import {
  MONATSNAMEN,
  STATUSTEXT,
  aktuelleStundeOffen,
  alsDauer,
  euro,
  heute,
  istUhrzeit,
  stundeSpaeterOffen,
  istWochenende,
  kurzesDatum,
  wochentag,
  zeitpunkt,
} from '../format.js'

// Der Monat, der beim Öffnen gezeigt wird.
function aktuellerMonat() {
  const jetzt = new Date()
  return { jahr: jetzt.getFullYear(), monat: jetzt.getMonth() + 1 }
}

export default function Monat() {
  const { benutzer } = useSitzung()
  const [parameter, setParameter] = useSearchParams()

  const start = aktuellerMonat()
  const jahr = Number(parameter.get('jahr')) || start.jahr
  const monat = Number(parameter.get('monat')) || start.monat
  const benutzerId = Number(parameter.get('benutzer')) || benutzer.id

  const [daten, setDaten] = useState(null)
  const [fehler, setFehler] = useState('')
  const [meldung, setMeldung] = useState('')
  const [mitarbeiter, setMitarbeiter] = useState([])
  const [beschaeftigt, setBeschaeftigt] = useState(false)
  const [neuOffen, setNeuOffen] = useState(false)

  const darfFremdeSehen = benutzer.rolle === 'vorgesetzter' || benutzer.rolle === 'admin'

  const laden = useCallback(() => {
    setFehler('')
    api(`/monat/${jahr}/${monat}?benutzer=${benutzerId}`)
      .then(setDaten)
      .catch((e) => {
        setDaten(null)
        setFehler(e.message)
      })
  }, [jahr, monat, benutzerId])

  useEffect(laden, [laden])

  useEffect(() => {
    if (!darfFremdeSehen) return
    api('/mitarbeiter')
      .then((d) => setMitarbeiter(d.mitarbeiter))
      .catch(() => setMitarbeiter([]))
  }, [darfFremdeSehen])

  function wechsle(neuesJahr, neuerMonat, neuerBenutzer = benutzerId) {
    const naechste = { jahr: String(neuesJahr), monat: String(neuerMonat) }
    if (neuerBenutzer !== benutzer.id) naechste.benutzer = String(neuerBenutzer)
    setParameter(naechste)
    setMeldung('')
  }

  function verschiebe(schritte) {
    const roh = monat - 1 + schritte
    wechsle(jahr + Math.floor(roh / 12), (((roh % 12) + 12) % 12) + 1)
  }

  // Jede schreibende Aktion lädt danach neu — die Sperrregeln und Summen
  // kommen vom Server, die raten wir hier nicht nach.
  async function fuehreAus(aktion) {
    setBeschaeftigt(true)
    setFehler('')
    try {
      await aktion()
      laden()
    } catch (e) {
      setFehler(e.message)
      throw e
    } finally {
      setBeschaeftigt(false)
    }
  }

  if (fehler && !daten) {
    return (
      <main className="huelle">
        <Kopfzeile />
        <div className="meldung fehler">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      </main>
    )
  }

  if (!daten) {
    return (
      <main className="huelle">
        <Kopfzeile />
        <p className="hinweis">Einen Moment …</p>
      </main>
    )
  }

  const tage = gruppiereNachTag(daten.eintraege)
  const eigenerMonat = daten.benutzer.id === benutzer.id
  const kannEinreichen =
    daten.erfasstZeiten &&
    daten.eintraege.length > 0 &&
    ['offen', 'zurueckgewiesen'].includes(daten.blatt.status) &&
    (eigenerMonat || darfFremdeSehen)
  const kannEntscheiden = darfFremdeSehen && daten.blatt.status === 'eingereicht'
  // Eine Freigabe lässt sich zurücknehmen, solange nicht abgerechnet wurde.
  const kannWiderrufen =
    darfFremdeSehen && daten.blatt.status === 'genehmigt' && !daten.blatt.abgerechnetAm
  const kannBerichtSenden = daten.blatt.angelegt && daten.blatt.status !== 'offen'

  return (
    <main className="huelle">
      <Kopfzeile />

      <div className="monatsleiste">
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={() => verschiebe(-1)}
          title="Vorheriger Monat"
        >
          <Icon name="links" />
          <span className="nur-vorlesen">Vorheriger Monat</span>
        </button>
        <h2 className="monatstitel">
          {MONATSNAMEN[monat - 1]} {jahr}
        </h2>
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={() => verschiebe(1)}
          title="Nächster Monat"
        >
          <Icon name="rechts" />
          <span className="nur-vorlesen">Nächster Monat</span>
        </button>
      </div>

      {darfFremdeSehen && mitarbeiter.length > 0 && (
        <div className="feld feld--breit">
          <label htmlFor="wer">Mitarbeiter</label>
          <select
            id="wer"
            value={benutzerId}
            onChange={(e) => wechsle(jahr, monat, Number(e.target.value))}
          >
            {mitarbeiter
              .filter((m) => m.rolle === 'mitarbeiter' || m.id === benutzerId)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.id === benutzer.id ? ' (ich)' : ''}
                </option>
              ))}
          </select>
        </div>
      )}

      <Standanzeige blatt={daten.blatt} />

      {meldung && (
        <div className="meldung ok" role="status">
          <Icon name="haken" groesse={18} />
          <span>{meldung}</span>
        </div>
      )}
      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <section className="karte summenkarte">
        <div className="summenzeile summenzeile--gross">
          <span>Erfasste Zeit</span>
          <strong>{alsDauer(daten.summeMinuten)}</strong>
        </div>
        {daten.stundenlohnCent !== null && (
          <>
            <div className="summenzeile">
              <span className="gedaempft">Stundenlohn</span>
              <span className="gedaempft">{euro(daten.stundenlohnCent)}</span>
            </div>
            <div className="summenzeile">
              <span>Verdienst</span>
              <strong>{euro(daten.verdienstCent)}</strong>
            </div>
          </>
        )}
        {daten.grenze && <Grenzhinweis daten={daten} />}
      </section>

      <section className="karte karte--liste">
        <h2>Erfasste Zeiten</h2>

        {tage.length === 0 && daten.erfasstZeiten && (
          <div className="leerzustand">
            <Icon name="kalender" groesse={32} strich={1.5} />
            <p>Für diesen Monat ist noch nichts erfasst.</p>
          </div>
        )}

        {!daten.erfasstZeiten && tage.length === 0 && (
          <div className="leerzustand">
            <Icon name="schloss" groesse={32} strich={1.5} />
            <p>
              {eigenerMonat
                ? 'Für Verwaltung und Vorgesetzte werden keine Zeiten erfasst.'
                : `${daten.benutzer.name} führt keinen Stundenzettel.`}
            </p>
            <p className="hinweis">
              Prüfen und freigeben ja, selbst erfassen nein — wer beides tun soll, braucht ein
              zweites Konto als Mitarbeiter.
            </p>
          </div>
        )}

        {tage.map((tag) => (
          <div className="tag" key={tag.datum}>
            <div className={`tageskopf ${istWochenende(tag.datum) ? 'tageskopf--frei' : ''}`}>
              <span className="tageskopf__datum">
                {kurzesDatum(tag.datum)}{' '}
                <span className="tageskopf__tag">{wochentag(tag.datum)}</span>
              </span>
              <strong>{alsDauer(tag.summe)}</strong>
            </div>
            {tag.eintraege.map((eintrag) => (
              <Zeile
                key={eintrag.id}
                eintrag={eintrag}
                bearbeitbar={daten.bearbeitbar && !beschaeftigt}
                grundNoetig={daten.grundNoetig}
                raster={daten.zeitRaster}
                taetigkeiten={daten.taetigkeiten}
                zielBenutzerId={daten.benutzer.id}
                beiListeGeaendert={laden}
                beiAenderung={fuehreAus}
              />
            ))}
          </div>
        ))}
      </section>

      {(kannEinreichen || kannEntscheiden || kannBerichtSenden || kannWiderrufen) && (
        <section className="karte">
          <h2>Monatsblatt</h2>
          {kannEinreichen && (
            <Einreichen
              daten={daten}
              jahr={jahr}
              monat={monat}
              benutzerId={benutzerId}
              eigenerMonat={eigenerMonat}
              beiFertig={(text) => {
                setMeldung(text)
                laden()
              }}
            />
          )}
          {kannBerichtSenden && (
            <Berichtversand
              jahr={jahr}
              monat={monat}
              benutzerId={benutzerId}
              blatt={daten.blatt}
              beiFertig={(text) => {
                setMeldung(text)
                laden()
              }}
              beiFehler={setFehler}
            />
          )}
          {kannWiderrufen && (
            <Widerruf
              jahr={jahr}
              monat={monat}
              benutzerId={benutzerId}
              beiFertig={(text) => {
                setMeldung(text)
                laden()
              }}
            />
          )}
          {kannEntscheiden && (
            <Entscheidung
              jahr={jahr}
              monat={monat}
              benutzerId={benutzerId}
              beschaeftigt={beschaeftigt}
              beiAktion={fuehreAus}
            />
          )}
        </section>
      )}

      <section className="karte">
        <h2>Export</h2>
        <div className="knopfreihe knopfreihe--gleich">
          <a
            className="knopf knopf--leise"
            href={`/api/export/${jahr}/${monat}.pdf?benutzer=${benutzerId}`}
          >
            <Icon name="dokument" /> PDF
          </a>
          <a
            className="knopf knopf--leise"
            href={`/api/export/${jahr}/${monat}.csv?benutzer=${benutzerId}`}
          >
            <Icon name="tabelle" /> CSV
          </a>
        </div>
      </section>

      {daten.bearbeitbar && (
        <button
          className="knopf knopf--haupt schwebeknopf"
          type="button"
          onClick={() => setNeuOffen(true)}
        >
          <Icon name="plus" groesse={22} strich={2.2} /> Zeit erfassen
        </button>
      )}

      {neuOffen && (
        <Dialog titel="Neue Zeit" symbol="plus" beiSchliessen={() => setNeuOffen(false)}>
          <Zeitformular
            vorgabeDatum={vorgabeDatumFuer(jahr, monat)}
            grundNoetig={daten.grundNoetig}
            raster={daten.zeitRaster}
            taetigkeiten={daten.taetigkeiten}
            zielBenutzerId={benutzerId}
            beiListeGeaendert={laden}
            beiAbbruch={() => setNeuOffen(false)}
            beiSpeichern={async (werte, grund) => {
              await fuehreAus(() =>
                api('/zeiten', { methode: 'POST', koerper: { ...werte, benutzerId, grund } })
              )
              setNeuOffen(false)
            }}
          />
        </Dialog>
      )}
    </main>
  )
}

// --- Bausteine --------------------------------------------------------------

function gruppiereNachTag(eintraege) {
  const nachTag = new Map()
  for (const eintrag of eintraege) {
    if (!nachTag.has(eintrag.datum)) nachTag.set(eintrag.datum, [])
    nachTag.get(eintrag.datum).push(eintrag)
  }
  return [...nachTag.entries()].map(([datum, liste]) => ({
    datum,
    eintraege: liste,
    summe: liste.reduce((s, e) => s + e.dauerMinuten, 0),
  }))
}

// Heute, falls der gezeigte Monat der laufende ist — sonst dessen Erster.
function vorgabeDatumFuer(jahr, monat) {
  const jetzt = new Date()
  return jetzt.getFullYear() === jahr && jetzt.getMonth() + 1 === monat
    ? heute()
    : `${jahr}-${String(monat).padStart(2, '0')}-01`
}

const STANDART = {
  offen: { klasse: 'stand--offen', symbol: 'stift' },
  eingereicht: { klasse: 'stand--wartet', symbol: 'schloss' },
  genehmigt: { klasse: 'stand--gut', symbol: 'haken' },
  zurueckgewiesen: { klasse: 'stand--achtung', symbol: 'zurueck' },
}

function Standanzeige({ blatt }) {
  const art = STANDART[blatt.status]

  return (
    <div className={`stand ${art.klasse}`}>
      <Icon name={art.symbol} groesse={18} />
      <div className="stand__inhalt">
        <div className="stand__zeile">
          <span className={`abzeichen abzeichen--${blatt.status}`}>{STATUSTEXT[blatt.status]}</span>
          {blatt.status === 'eingereicht' && blatt.eingereichtAm && (
            <span className="gedaempft">seit {zeitpunkt(blatt.eingereichtAm)}</span>
          )}
          {blatt.status === 'genehmigt' && blatt.geprueftVonName && (
            <span className="gedaempft">durch {blatt.geprueftVonName}</span>
          )}
        </div>
        {blatt.status === 'zurueckgewiesen' && blatt.kommentar && (
          <p className="hinweis">„{blatt.kommentar}“</p>
        )}
        {blatt.unterschriftAm && (
          <p className="hinweis">Unterschrieben am {zeitpunkt(blatt.unterschriftAm)}</p>
        )}
        {blatt.berichtVersandtAm && (
          <p className="hinweis">
            Bericht am {zeitpunkt(blatt.berichtVersandtAm)} an {blatt.berichtAn} verschickt
          </p>
        )}
        {blatt.abgerechnetAm && (
          <p className="hinweis">Abgerechnet am {zeitpunkt(blatt.abgerechnetAm)}</p>
        )}
      </div>
    </div>
  )
}

function Grenzhinweis({ daten }) {
  const schwelle = (daten.grenze.warnschwelle ?? 90) / 100
  const anteil = daten.verdienstCent / daten.grenze.betragCent
  if (!daten.verdienstCent || anteil < schwelle) return null

  const drueber = daten.verdienstCent > daten.grenze.betragCent
  return (
    <p className={`meldung ${drueber ? 'fehler' : 'warnung'}`} style={{ margin: '.75rem 0 0' }}>
      <Icon name="warnung" groesse={18} />
      <span>
        {drueber
          ? `Über der hinterlegten Verdienstgrenze von ${euro(daten.grenze.betragCent)}.`
          : `Nahe an der hinterlegten Verdienstgrenze von ${euro(daten.grenze.betragCent)}.`}
        {!daten.grenze.bestaetigt && ' Der Grenzwert ist noch nicht bestätigt.'}
      </span>
    </p>
  )
}

function Zeile({
  eintrag,
  bearbeitbar,
  grundNoetig,
  raster,
  taetigkeiten,
  zielBenutzerId,
  beiListeGeaendert,
  beiAenderung,
}) {
  const [bearbeiten, setBearbeiten] = useState(false)
  const [loeschen, setLoeschen] = useState(false)
  const [verlauf, setVerlauf] = useState(null)

  async function zeigeVerlauf() {
    if (verlauf) return setVerlauf(null)
    const daten = await api(`/zeiten/${eintrag.id}/verlauf`)
    setVerlauf(daten.verlauf)
  }

  return (
    <>
      <div className="eintrag">
        <div className="eintrag__zeit">
          {eintrag.beginn}–{eintrag.ende}
        </div>
        <div className="eintrag__text">
          <span>{eintrag.taetigkeit || <span className="gedaempft">ohne Angabe</span>}</span>
          {eintrag.notiz && <small>{eintrag.notiz}</small>}
          {eintrag.geaendertVonName && <small>geändert von {eintrag.geaendertVonName}</small>}
        </div>
        <div className="eintrag__dauer">{alsDauer(eintrag.dauerMinuten)}</div>
        <div className="eintrag__aktionen">
          <button
            className="knopf knopf--geist knopf--symbol knopf--klein"
            type="button"
            onClick={zeigeVerlauf}
            title="Änderungsverlauf"
          >
            <Icon name="verlauf" groesse={18} />
            <span className="nur-vorlesen">Änderungsverlauf</span>
          </button>
          {bearbeitbar && (
            <>
              <button
                className="knopf knopf--geist knopf--symbol knopf--klein"
                type="button"
                onClick={() => setBearbeiten(true)}
                title="Ändern"
              >
                <Icon name="stift" groesse={18} />
                <span className="nur-vorlesen">Ändern</span>
              </button>
              <button
                className="knopf knopf--geist knopf--symbol knopf--klein"
                type="button"
                onClick={() => setLoeschen(true)}
                title="Löschen"
              >
                <Icon name="muell" groesse={18} />
                <span className="nur-vorlesen">Löschen</span>
              </button>
            </>
          )}
        </div>
      </div>

      {verlauf && (
        <ul className="verlauf">
          {verlauf.length === 0 && <li>Keine Änderungen.</li>}
          {verlauf.map((z) => (
            <li key={z.id}>
              {zeitpunkt(z.zeitpunkt)} · {z.aktion}
              {z.feld && ` · ${z.feld}: ${z.alt ?? '—'} → ${z.neu ?? '—'}`}
              {z.akteur_name && ` · ${z.akteur_name}`}
              {z.grund && ` · „${z.grund}“`}
            </li>
          ))}
        </ul>
      )}

      {bearbeiten && (
        <Dialog
          titel="Zeit ändern"
          symbol="stift"
          untertitel={`${kurzesDatum(eintrag.datum)} ${eintrag.beginn}–${eintrag.ende}`}
          beiSchliessen={() => setBearbeiten(false)}
        >
          <Zeitformular
            anfangswerte={eintrag}
            grundNoetig={grundNoetig}
            raster={raster}
            taetigkeiten={taetigkeiten}
            zielBenutzerId={zielBenutzerId}
            beiListeGeaendert={beiListeGeaendert}
            beiAbbruch={() => setBearbeiten(false)}
            beiSpeichern={async (werte, grund) => {
              await beiAenderung(() =>
                api(`/zeiten/${eintrag.id}`, { methode: 'PATCH', koerper: { ...werte, grund } })
              )
              setBearbeiten(false)
            }}
          />
        </Dialog>
      )}

      {loeschen && (
        <Loeschdialog
          eintrag={eintrag}
          grundNoetig={grundNoetig}
          beiAbbruch={() => setLoeschen(false)}
          beiLoeschen={async (grund) => {
            await beiAenderung(() =>
              api(`/zeiten/${eintrag.id}`, { methode: 'DELETE', koerper: { grund } })
            )
            setLoeschen(false)
          }}
        />
      )}
    </>
  )
}

function Loeschdialog({ eintrag, grundNoetig, beiLoeschen, beiAbbruch }) {
  const [grund, setGrund] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')

  async function ausfuehren() {
    setLaeuft(true)
    setFehler('')
    try {
      await beiLoeschen(grund)
    } catch (e) {
      setFehler(e.message)
      setLaeuft(false)
    }
  }

  return (
    <Dialog titel="Zeit löschen" symbol="muell" beiSchliessen={() => !laeuft && beiAbbruch()}>
      <p>
        <strong>
          {kurzesDatum(eintrag.datum)} {eintrag.beginn}–{eintrag.ende}
        </strong>
        {eintrag.taetigkeit ? ` · ${eintrag.taetigkeit}` : ''}
      </p>
      <p className="hinweis">
        Der Eintrag zählt danach nicht mehr mit. Endgültig gelöscht wird nichts — er bleibt im
        Änderungsverlauf nachvollziehbar.
      </p>

      {grundNoetig && (
        <div className="feld" style={{ marginTop: '1rem' }}>
          <label htmlFor="loeschgrund">Grund</label>
          <input
            id="loeschgrund"
            type="text"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="wird im Änderungsprotokoll festgehalten"
          />
        </div>
      )}

      {fehler && (
        <div className="meldung fehler" role="alert" style={{ marginTop: '1rem' }}>
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <div className="dialog__fuss">
        <button className="knopf knopf--leise" type="button" onClick={beiAbbruch} disabled={laeuft}>
          Abbrechen
        </button>
        <button className="knopf knopf--gefahr" type="button" onClick={ausfuehren} disabled={laeuft}>
          <Icon name="muell" /> {laeuft ? 'Wird gelöscht …' : 'Löschen'}
        </button>
      </div>
    </Dialog>
  )
}

// Formular für neuen Eintrag und zum Ändern — dieselben Felder, dieselben Regeln.
function Zeitformular({
  anfangswerte,
  grundNoetig,
  raster,
  taetigkeiten = [],
  zielBenutzerId,
  beiListeGeaendert,
  beiSpeichern,
  beiAbbruch,
  vorgabeDatum,
}) {
  // Bei einem neuen Eintrag steht die Stunde schon da — jetzt und eine
  // Stunde später —, die Minuten bleiben offen. So beginnt das Auswahlrad an
  // der richtigen Stelle statt bei 00, ohne eine Minute zu erfinden.
  const startVorschlag = anfangswerte?.beginn || aktuelleStundeOffen()
  const [datum, setDatum] = useState(anfangswerte?.datum || vorgabeDatum || heute())
  const [beginn, setBeginn] = useState(startVorschlag)
  const [ende, setEnde] = useState(anfangswerte?.ende || stundeSpaeterOffen(startVorschlag))
  const [taetigkeit, setTaetigkeit] = useState(anfangswerte?.taetigkeit || '')
  const [notiz, setNotiz] = useState(anfangswerte?.notiz || '')
  const [grund, setGrund] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')

  async function absenden(ereignis) {
    ereignis.preventDefault()
    setLaeuft(true)
    setFehler('')
    try {
      await beiSpeichern({ datum, beginn, ende, taetigkeit, notiz }, grund)
    } catch (e) {
      setFehler(e.message)
      setLaeuft(false)
    }
  }

  // Beim Tippen bleiben nur die Vorschläge stehen, die dazu passen — als
  // Anfang des ganzen Textes oder eines Wortes darin. So findet „Anp“ auch
  // „Visitenkarten Anpassung“.
  const suche = taetigkeit.trim().toLowerCase()
  const passende = suche
    ? taetigkeiten.filter((t) => {
        const klein = t.toLowerCase()
        if (klein === suche) return false // schon getippt, kein Vorschlag nötig
        return klein.startsWith(suche) || klein.split(/[\s/(-]+/).some((w) => w.startsWith(suche))
      })
    : taetigkeiten

  async function verbergen(text) {
    setFehler('')
    try {
      await api('/taetigkeiten/verbergen', {
        methode: 'POST',
        koerper: { benutzerId: zielBenutzerId, taetigkeit: text },
      })
      beiListeGeaendert?.()
    } catch (e) {
      setFehler(e.message)
    }
  }

  return (
    <form onSubmit={absenden}>
      <div className="feld">
        <label htmlFor="datum">Datum</label>
        <input
          id="datum"
          type="date"
          required
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
        />
      </div>

      <div className="formularreihe" style={{ marginBottom: '1rem' }}>
        <div className="feld feld--schmal">
          <span className="feldbeschriftung">Von</span>
          <Uhrzeitwahl wert={beginn} raster={raster || 15} pflicht beiAenderung={setBeginn} />
        </div>
        <div className="feld feld--schmal">
          <span className="feldbeschriftung">Bis</span>
          <Uhrzeitwahl wert={ende} raster={raster || 15} pflicht beiAenderung={setEnde} />
        </div>
      </div>

      <div className="feld">
        <label htmlFor="taetigkeit">Tätigkeit</label>
        <input
          id="taetigkeit"
          type="text"
          value={taetigkeit}
          maxLength={200}
          list="taetigkeitsliste"
          autoComplete="off"
          onChange={(e) => setTaetigkeit(e.target.value)}
        />
        {/* Die Liste füllt die Vorschläge des Geräts beim Tippen. */}
        <datalist id="taetigkeitsliste">
          {taetigkeiten.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>

        {/* Dieselben Vorschläge zum Antippen — auf dem Handy schneller als
            jede Vorschlagsliste, und sie lassen sich hier auch entfernen. */}
        {passende.length > 0 && (
          <div className="marken">
            {passende.slice(0, 6).map((t) => (
              <span
                key={t}
                className={`marke-knopf ${t === taetigkeit ? 'marke-knopf--an' : ''}`}
              >
                <button
                  type="button"
                  className="marke-knopf__text"
                  onClick={() => setTaetigkeit(t === taetigkeit ? '' : t)}
                >
                  {t}
                </button>
                <button
                  type="button"
                  className="marke-knopf__weg"
                  title={`„${t}“ nicht mehr vorschlagen`}
                  onClick={() => verbergen(t)}
                >
                  <Icon name="kreuz" groesse={13} strich={2.4} />
                  <span className="nur-vorlesen">„{t}“ nicht mehr vorschlagen</span>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="feld">
        <label htmlFor="notiz">Notiz</label>
        <input
          id="notiz"
          type="text"
          value={notiz}
          maxLength={1000}
          onChange={(e) => setNotiz(e.target.value)}
        />
      </div>

      {grundNoetig && (
        <div className="feld">
          <label htmlFor="grund">Grund der Änderung</label>
          <input
            id="grund"
            type="text"
            required
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="wird im Änderungsprotokoll festgehalten"
          />
        </div>
      )}

      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <div className="dialog__fuss">
        <button className="knopf knopf--leise" type="button" onClick={beiAbbruch} disabled={laeuft}>
          Abbrechen
        </button>
        <button className="knopf knopf--haupt" type="submit" disabled={laeuft || !istUhrzeit(beginn) || !istUhrzeit(ende)}>
          <Icon name="haken" /> {laeuft ? 'Speichert …' : 'Speichern'}
        </button>
      </div>
    </form>
  )
}

function Einreichen({ daten, jahr, monat, benutzerId, eigenerMonat, beiFertig }) {
  const [offen, setOffen] = useState(false)
  const [unterschrift, setUnterschrift] = useState(null)
  const [grund, setGrund] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')

  const tage = new Set(daten.eintraege.map((e) => e.datum)).size

  async function einreichen() {
    setFehler('')
    setLaeuft(true)
    try {
      const antwort = await api(`/monat/${jahr}/${monat}/einreichen`, {
        methode: 'POST',
        koerper: { benutzerId, grund, unterschrift },
      })
      setOffen(false)
      beiFertig(
        antwort.bericht?.versandt
          ? `Monat eingereicht. Der Bericht ist an ${antwort.bericht.an} unterwegs.`
          : `Monat eingereicht. Der Bericht wurde nicht verschickt: ${antwort.bericht?.grund || 'unbekannter Grund'}`
      )
    } catch (e) {
      setFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="aktionsblock">
      <p className="hinweis hinweis--oben">
        {alsDauer(daten.summeMinuten)} an {tage} {tage === 1 ? 'Tag' : 'Tagen'} erfasst.
      </p>

      <button
        className="knopf knopf--haupt knopf--breit"
        type="button"
        onClick={() => setOffen(true)}
      >
        <Icon name="unterschrift" /> Monat abschließen und einreichen
      </button>

      {offen && (
        <Dialog
          titel="Monat einreichen"
          symbol="unterschrift"
          untertitel={`${MONATSNAMEN[monat - 1]} ${jahr} · ${daten.benutzer.name}`}
          beiSchliessen={() => !laeuft && setOffen(false)}
        >
          <div className="summenkarte">
            <div className="summenzeile summenzeile--gross">
              <span>Erfasste Zeit</span>
              <strong>{alsDauer(daten.summeMinuten)}</strong>
            </div>
            <div className="summenzeile">
              <span className="gedaempft">Tage mit Einträgen</span>
              <span className="gedaempft">{tage}</span>
            </div>
            {daten.verdienstCent !== null && daten.verdienstCent !== undefined && (
              <div className="summenzeile">
                <span className="gedaempft">Verdienst</span>
                <span className="gedaempft">{euro(daten.verdienstCent)}</span>
              </div>
            )}
          </div>

          <p className="hinweis">
            Mit dem Einreichen ist der Monat für dich abgeschlossen — ändern kann ihn danach nur
            noch die Verwaltung. Das unterschriebene Blatt geht als PDF automatisch raus.
          </p>

          {eigenerMonat ? (
            <>
              <p className="unterschrift__titel">Unterschrift</p>
              <Unterschrift beiAenderung={setUnterschrift} />
            </>
          ) : (
            <div className="feld" style={{ marginTop: '1rem' }}>
              <label htmlFor="einreichgrund">Grund</label>
              <input
                id="einreichgrund"
                type="text"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                placeholder="warum du für jemand anderen einreichst"
              />
              <p className="hinweis">
                Für eine fremde Person kannst du nicht unterschreiben — das Blatt bleibt an dieser
                Stelle leer, dein Grund steht im Protokoll.
              </p>
            </div>
          )}

          {fehler && (
            <div className="meldung fehler" role="alert" style={{ marginTop: '1rem' }}>
              <Icon name="warnung" groesse={18} />
              <span>{fehler}</span>
            </div>
          )}

          <div className="dialog__fuss">
            <button
              className="knopf knopf--leise"
              type="button"
              onClick={() => setOffen(false)}
              disabled={laeuft}
            >
              Abbrechen
            </button>
            <button
              className="knopf knopf--haupt"
              type="button"
              onClick={einreichen}
              disabled={laeuft || (eigenerMonat && !unterschrift)}
            >
              <Icon name="haken" /> {laeuft ? 'Wird eingereicht …' : 'Einreichen'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

function Entscheidung({ jahr, monat, benutzerId, beschaeftigt, beiAktion }) {
  const [kommentar, setKommentar] = useState('')

  function entscheide(entscheidung) {
    return beiAktion(() =>
      api(`/monat/${jahr}/${monat}/entscheidung`, {
        methode: 'POST',
        koerper: { benutzerId, entscheidung, kommentar },
      })
    ).catch(() => {})
  }

  return (
    <div className="aktionsblock">
      <p className="hinweis hinweis--oben">
        Mit der Freigabe geht der unterschriebene Beleg an die Lohnbuchhaltung und der Monat
        erscheint in der Abrechnung.
      </p>
      <div className="feld">
        <label htmlFor="kommentar">Kommentar (bei Rückweisung erforderlich)</label>
        <input
          id="kommentar"
          type="text"
          value={kommentar}
          onChange={(e) => setKommentar(e.target.value)}
        />
      </div>
      <div className="knopfreihe knopfreihe--gleich">
        <button
          className="knopf knopf--haupt"
          type="button"
          disabled={beschaeftigt}
          onClick={() => entscheide('genehmigen')}
        >
          <Icon name="daumen" /> Genehmigen
        </button>
        <button
          className="knopf knopf--leise"
          type="button"
          disabled={beschaeftigt}
          onClick={() => entscheide('zurueckweisen')}
        >
          <Icon name="zurueck" /> Zurückweisen
        </button>
      </div>
    </div>
  )
}

// Verschickt das unterschriebene Blatt (erneut) an die hinterlegte Adresse.
function Berichtversand({ jahr, monat, benutzerId, blatt, beiFertig, beiFehler }) {
  const [laeuft, setLaeuft] = useState(false)

  async function senden() {
    setLaeuft(true)
    try {
      const antwort = await api(`/monat/${jahr}/${monat}/bericht`, {
        methode: 'POST',
        koerper: { benutzerId },
      })
      beiFertig(`Bericht an ${antwort.bericht.an} verschickt.`)
    } catch (e) {
      beiFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="aktionsblock">
      <p className="hinweis hinweis--oben">
        {blatt.berichtVersandtAm
          ? `Bericht am ${zeitpunkt(blatt.berichtVersandtAm)} an ${blatt.berichtAn} verschickt.`
          : 'Der Bericht wurde für diesen Monat noch nicht verschickt.'}
      </p>
      <button className="knopf knopf--leise" type="button" disabled={laeuft} onClick={senden}>
        <Icon name="senden" />
        {laeuft ? 'Wird verschickt …' : blatt.berichtVersandtAm ? 'Erneut senden' : 'Bericht senden'}
      </button>
    </div>
  )
}

// Nimmt eine erteilte Freigabe zurück — etwa wenn kurz danach auffällt, dass
// ein Tag fehlt. Nach der Abrechnung sperrt der Server das ab.
function Widerruf({ jahr, monat, benutzerId, beiFertig }) {
  const [offen, setOffen] = useState(false)
  const [grund, setGrund] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')

  async function widerrufen() {
    setLaeuft(true)
    setFehler('')
    try {
      await api(`/monat/${jahr}/${monat}/entscheidung`, {
        methode: 'POST',
        koerper: { benutzerId, entscheidung: 'widerrufen', kommentar: grund },
      })
      setOffen(false)
      beiFertig('Die Freigabe wurde widerrufen. Der Monat steht wieder zur Prüfung.')
    } catch (e) {
      setFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="aktionsblock">
      <p className="hinweis hinweis--oben">
        Der Monat ist freigegeben. Solange er nicht abgerechnet ist, lässt sich das zurücknehmen.
      </p>
      <button className="knopf knopf--leise" type="button" onClick={() => setOffen(true)}>
        <Icon name="zurueck" /> Freigabe widerrufen
      </button>

      {offen && (
        <Dialog titel="Freigabe widerrufen" symbol="zurueck" beiSchliessen={() => !laeuft && setOffen(false)}>
          <p className="hinweis hinweis--oben">
            Das Blatt geht zurück auf „eingereicht“ und kann erneut geprüft werden. Der Mitarbeiter
            kann weiterhin nichts ändern — dafür müsstest du es zurückweisen.
          </p>
          <div className="feld">
            <label htmlFor="widerrufsgrund">Grund</label>
            <input
              id="widerrufsgrund"
              type="text"
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              placeholder="wird im Änderungsprotokoll festgehalten"
            />
          </div>
          {fehler && (
            <div className="meldung fehler" role="alert">
              <Icon name="warnung" groesse={18} />
              <span>{fehler}</span>
            </div>
          )}
          <div className="dialog__fuss">
            <button className="knopf knopf--leise" type="button" onClick={() => setOffen(false)} disabled={laeuft}>
              Abbrechen
            </button>
            <button
              className="knopf knopf--haupt"
              type="button"
              onClick={widerrufen}
              disabled={laeuft || grund.trim().length < 3}
            >
              <Icon name="zurueck" /> {laeuft ? 'Wird widerrufen …' : 'Widerrufen'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}
